import { InvoiceLineItem } from '@/types/invoice';
import { DEFAULT_TAX_CONFIG, netFromGross, taxFromNet, type TaxConfig } from './tax';
import { partsSellTotal, round2 } from './partsPricing';

export interface JobFinancialsForInvoice {
  /** Customer-facing description of the work performed. The raw internal
   * issue_description (dispatch notes) must never be passed here. */
  work_summary?: string | null;
  /** Gross total charged to the customer — the operator-entered job revenue. */
  invoice_total: number;
}

/** Fallback labor-line description when no customer-facing summary was entered. */
export const DEFAULT_WORK_DESCRIPTION = 'Service call — work completed on site';

export interface PartForInvoice {
  part_name: string;
  /** Customer-facing unit price, same tax basis as invoice_total.
   * Never pass part_cost here — the internal cost must not reach the invoice. */
  sell_price: number;
  quantity: number;
}

/** True when the parts charged to the customer add up to more than the job total. */
export function partsExceedTotal(
  job: Pick<JobFinancialsForInvoice, 'invoice_total'>,
  parts: PartForInvoice[],
): boolean {
  return partsSellTotal(parts) > round2(job.invoice_total) + 0.005;
}

/**
 * Builds invoice line items from job financials and parts.
 * - Labor description uses the customer-facing work summary; the raw internal
 *   issue_description is intentionally never placed on the invoice
 * - invoice_total and part sell prices are gross (tax-inclusive) figures; every
 *   line is stored ex-tax so that summing the lines and re-adding tax
 *   (calculateInvoiceTotals) reproduces exactly the invoice_total the customer
 *   was charged
 * - The labor line is invoice_total minus the parts at their SELL price, so the
 *   markup stays on the parts lines. If the parts alone exceed the total the labor
 *   line is omitted (callers validate with partsExceedTotal before this point)
 * - Part cost, tolls and card fees are internal profit inputs and never appear on
 *   the customer invoice
 */
export function buildInvoiceLineItems(
  job: JobFinancialsForInvoice,
  parts: PartForInvoice[],
  taxConfig: TaxConfig = DEFAULT_TAX_CONFIG,
): InvoiceLineItem[] {
  const items: InvoiceLineItem[] = [];

  const laborAmount = job.invoice_total - partsSellTotal(parts);
  if (laborAmount > 0) {
    const laborExTax = netFromGross(laborAmount, taxConfig);
    items.push({
      description: job.work_summary?.trim() || DEFAULT_WORK_DESCRIPTION,
      quantity: 1,
      unit_price: laborExTax,
      amount: laborExTax,
    });
  }

  parts.forEach(part => {
    const unitExTax = netFromGross(part.sell_price, taxConfig);
    items.push({
      description: `Parts - ${part.part_name}`,
      quantity: part.quantity,
      unit_price: unitExTax,
      amount: part.quantity * unitExTax,
    });
  });

  return items;
}

/**
 * Calculates invoice totals from line items.
 * Subtotal is the sum of line item amounts (ex-tax); tax is added on top.
 */
export function calculateInvoiceTotals(
  lineItems: InvoiceLineItem[],
  taxConfig: TaxConfig = DEFAULT_TAX_CONFIG,
) {
  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const gstAmount = taxFromNet(subtotal, taxConfig);
  const totalAmount = subtotal + gstAmount;
  return { subtotal, gstAmount, totalAmount };
}
